import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { 
  User, Shield, AlertTriangle, FileSearch, Server, UserCheck,
  ChevronDown, ChevronUp, FileText, ArrowRight, CheckCircle2, XCircle
} from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";

const AGENT_ICONS = {
  UserCheck: UserCheck,
  AlertTriangle: AlertTriangle,
  FileSearch: FileSearch,
  Shield: Shield,
  Server: Server,
  User: User
};

const RISK_COLORS = {
  Critical: "bg-red-600 text-white",
  High: "bg-red-500 text-white",
  Medium: "bg-amber-500 text-white",
  Low: "bg-blue-500 text-white",
  Observation: "bg-slate-500 text-white"
};

const AGENT_COLORS = {
  lead_auditor: "border-l-emerald-500",
  skeptical_watchdog: "border-l-amber-500",
  detail_perfectionist: "border-l-blue-500",
  compliance_guardian: "border-l-purple-500",
  it_specialist: "border-l-cyan-500"
};

export function AgentInsightsFeed({ observations = [], onValidate, onDispute }) {
  const [expandedCards, setExpandedCards] = useState({});

  const toggleCard = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (observations.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 text-center">
          <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No agent observations yet. Run team analysis to generate insights.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Group observations by agent
  const groupedByAgent = observations.reduce((acc, obs) => {
    const key = obs.agent_id || "unknown";
    if (!acc[key]) acc[key] = [];
    acc[key].push(obs);
    return acc;
  }, {});

  return (
    <div className="space-y-4" data-testid="agent-insights-feed">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Agent Insights ({observations.length})</h3>
        <Badge variant="outline" className="text-xs">
          {Object.keys(groupedByAgent).length} agents contributed
        </Badge>
      </div>

      <div className="space-y-3">
        {observations.map((obs) => {
          const IconComponent = AGENT_ICONS[obs.agent_role] || User;
          const isExpanded = expandedCards[obs.id];
          const borderColor = AGENT_COLORS[obs.agent_role] || "border-l-slate-500";
          const riskColor = RISK_COLORS[obs.risk_rating] || RISK_COLORS.Medium;

          return (
            <Card 
              key={obs.id} 
              className={`border-l-4 ${borderColor}`}
              data-testid={`observation-${obs.id}`}
            >
              <Collapsible open={isExpanded} onOpenChange={() => toggleCard(obs.id)}>
                <CardHeader className="py-3 cursor-pointer" onClick={() => toggleCard(obs.id)}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3">
                      <IconComponent className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                      <div>
                        <CardTitle className="text-sm font-medium">{obs.title}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {obs.agent_name} • {new Date(obs.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`text-xs ${riskColor}`}>{obs.risk_rating}</Badge>
                      <Badge variant="outline" className="text-xs">
                        {Math.round(obs.confidence_score * 100)}% conf
                      </Badge>
                      <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                  </div>
                </CardHeader>

                <CollapsibleContent>
                  <CardContent className="pt-0 pb-3">
                    <div className="space-y-3">
                      {/* Observation Text */}
                      <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                        {obs.observation}
                      </div>

                      {/* Evidence References */}
                      {obs.evidence_refs?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium mb-1">Evidence References:</p>
                          <div className="flex flex-wrap gap-1">
                            {obs.evidence_refs.map((ref, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                <FileText className="w-3 h-3 mr-1" /> {ref}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recommended Next Steps */}
                      {obs.recommended_next_steps?.length > 0 && (
                        <div>
                          <p className="text-xs font-medium mb-1">Recommended Next Steps:</p>
                          <ul className="space-y-1">
                            {obs.recommended_next_steps.map((step, i) => (
                              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                                <ArrowRight className="w-3 h-3 mt-0.5 shrink-0" />
                                {step}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Status & Actions */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <Badge variant={obs.status === "validated" ? "default" : "outline"} className="text-xs">
                          {obs.status}
                        </Badge>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 text-xs"
                            onClick={() => onValidate?.(obs.id)}
                          >
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Validate
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 text-xs text-amber-600"
                            onClick={() => onDispute?.(obs.id)}
                          >
                            <XCircle className="w-3 h-3 mr-1" /> Dispute
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default AgentInsightsFeed;
